import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2, Settings, ShieldCheck, Zap, Rocket, Star, Info, Monitor, Smartphone, LayoutGrid, ListChecks, HelpCircle, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/tutoriais-iptv/configuracao")({
  head: () => ({
    meta: [
      { title: "Configuração de Aplicativos IPTV: Guia Completo Passo a Passo" },
      { name: "description", content: "Aprenda como configurar qualquer aplicativo IPTV em minutos. Guia definitivo para Smart TVs, TV Box, Android e iPhone com Painel IPTV Pós Pago." },
    ],
  }),
  component: ConfigTutorial,
});

function ConfigTutorial() {
  const categories = [
    {
      title: "Smart TVs",
      icon: <Monitor className="w-6 h-6" />,
      apps: ["One X", "Blessed", "Fun Play", "Lazer Play", "Assist Plus", "PlaySim", "VuPlayer", "IBO Player"],
      path: "/tutoriais-iptv/samsung"
    },
    {
      title: "Android & TV Box",
      icon: <LayoutGrid className="w-6 h-6" />,
      apps: ["OneX Player", "RP725", "VUser", "P2PRO", "P2PLAY"],
      path: "/tutoriais-iptv/tv-box"
    },
    {
      title: "iPhone & iPad",
      icon: <Smartphone className="w-6 h-6" />,
      apps: ["Smarters Player Lite", "Vizzion", "IBO Pro Player", "IPTV Player.io"],
      path: "/tutoriais-iptv/iphone"
    }
  ];

  const steps = [
    { title: "Instalação", description: "Baixe o app na loja oficial do seu dispositivo (Play Store, App Store ou TV Store)." },
    { title: "Abertura", description: "Inicie o aplicativo e localize o campo de login (geralmente Usuário e Senha)." },
    { title: "Credenciais", description: "Insira os dados fornecidos pelo seu revendedor do Painel Pós Pago." },
    { title: "Sincronização", description: "Aguarde alguns segundos para o carregamento da lista de canais e filmes." }
  ];

  return (
    <SEOPage
      title="Configuração de Aplicativos IPTV: Guia Definitivo 2026"
      subtitle="Descubra como configurar qualquer player em menos de 5 minutos com nossa estrutura profissional."
      category="Configuração Geral"
      introParagraphs={[
        "Muitas pessoas acreditam que configurar um aplicativo IPTV é um processo complicado, mas a verdade é que a tecnologia evoluiu para tornar essa tarefa rápida e automática. Em 2026, os players modernos são desenhados para que qualquer pessoa consiga realizar o setup sem ajuda técnica.",
        "Seja em Smart TVs, TV Box, Android, iPhone ou Roku TV, a configuração exige apenas as informações básicas fornecidas pelo seu revendedor. Nossa infraestrutura de Painel IPTV Pós Pago garante que essa conexão seja instantânea e estável."
      ]}
      breadcrumbs={[
        { label: "Início", href: "/" },
        { label: "Tutoriais", href: "/tutoriais-iptv" },
        { label: "Configuração", href: "/tutoriais-iptv/configuracao" }
      ]}
      benefits={[
        { title: "Setup Simplificado", description: "Esqueça configurações complexas de rede. É só logar e assistir." },
        { title: "Multi-Dispositivo", description: "Uma única estrutura compatível com todos os seus aparelhos." },
        { title: "Ativação em Tempo Real", description: "Liberação de sinal automática através do nosso painel." },
        { title: "Suporte Especializado", description: "Time técnico disponível para tirar dúvidas de configuração." },
        { title: "Segurança de Dados", description: "Conexão criptografada entre seu app e nossos servidores." },
        { title: "Atualizações", description: "Apps sempre em dia com as últimas funções de streaming." }
      ]}
      howItWorks={steps}
      diferenciais={[
        { title: "Verifique a Internet", description: "Uma conexão estável de pelo menos 10Mbps garante o melhor setup." },
        { title: "Apps Oficiais", description: "Sempre prefira baixar os aplicativos direto das lojas oficiais." },
        { title: "Siga os Guias", description: "Cada player tem suas particularidades; use nossos tutoriais específicos." },
        { title: "Dados de Acesso", description: "Mantenha seu usuário e senha sempre à mão para login rápido." }
      ]}
      faq={[
        { question: "Onde encontro os dados de acesso?", answer: "Seu revendedor IPTV fornecerá seu usuário, senha e, se necessário, a URL do servidor." },
        { question: "Posso usar em mais de uma TV?", answer: "Sim, dependendo do seu plano no Painel Pós Pago, você pode ter múltiplas telas simultâneas." },
        { question: "A configuração muda de um app para outro?", answer: "O conceito é o mesmo, mas a interface de login pode variar levemente entre players como OneX e Smarters." },
        { question: "E se o app pedir código MAC?", answer: "Alguns apps de Smart TV pedem o MAC. Basta enviá-lo ao seu revendedor para ativação remota." },
        { question: "Como atualizo a lista de canais?", answer: "Na maioria dos apps, basta reiniciar o aplicativo ou clicar em 'Refresh' nas configurações." }
      ]}
      sections={[
        {
          title: "Escolha Seu Dispositivo para Tutorial Específico",
          type: "grid",
          content: (
            <>
              {categories.map((cat, i) => (
                <div key={i} className="p-8 rounded-[2rem] bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all flex flex-col h-full">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="p-3 bg-electric/10 rounded-2xl text-electric">
                      {cat.icon}
                    </div>
                    <h3 className="text-xl font-bold">{cat.title}</h3>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-8">
                    {cat.apps.map((app, idx) => (
                      <span key={idx} className="text-[10px] font-bold px-3 py-1 bg-white/5 rounded-full text-muted-foreground italic">
                        {app}
                      </span>
                    ))}
                  </div>
                  <Link 
                    to={cat.path as any}
                    className="mt-auto inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-electric hover:gap-4 transition-all"
                  >
                    Ver Passo a Passo <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ))}
            </>
          )
        },
        {
          title: "Suporte e Solução de Problemas",
          type: "featured",
          content: (
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="p-6 bg-background rounded-3xl border border-white/5">
                  <HelpCircle className="w-8 h-8 text-electric mb-4" />
                  <h4 className="font-bold text-lg mb-2">Está com dificuldades?</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Nossa equipe de suporte técnico é treinada para auxiliar em qualquer cenário, desde Smart TVs LG/Samsung até configurações avançadas em Fire Stick.
                  </p>
                </div>
                <div className="flex gap-4 items-center">
                  <div className="w-12 h-12 bg-electric/10 rounded-full flex items-center justify-center text-electric shrink-0">
                    <Zap className="w-6 h-6" />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Atendimento humanizado via WhatsApp para revendedores.</p>
                </div>
              </div>
              <div className="bg-electric/10 p-8 rounded-[3rem] border border-electric/20">
                <h4 className="text-xl font-black italic uppercase mb-4 tracking-tighter">Checklist de Sucesso</h4>
                <ul className="space-y-4">
                  {[
                    "Cabo de rede (preferível ao Wi-Fi)",
                    "App atualizado na última versão",
                    "Dados de acesso corretos",
                    "DNS da operadora desbloqueado"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-electric" />
                      <span className="text-sm font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )
        }
      ]}
      ctaTitle="🚀 Quer Facilitar a Vida dos Seus Clientes?"
      ctaDescription="Trabalhe com a estrutura de Painel IPTV Pós Pago que oferece os aplicativos mais fáceis de configurar do mercado."
    />
  );
}
